import { html } from '/vendor/htm-preact.module.js';

/**
 * How wide the app is allowed to get — GitHub issue #52.
 *
 * PORTRAIT IS THE DEFAULT AND STAYS THE DEFAULT. This app is used standing at
 * a table with a phone in one hand, and `#app { max-width: 30rem }` is what
 * makes that work; the comment on that rule records an iPhone getting
 * letterboxed with 40px of dead margin before it existed. Nothing here
 * changes what anybody sees until they ask for it.
 *
 * A ROOT ATTRIBUTE, not a class on some component. The cap is on `#app`, and
 * the chrome hangs off it with negative margins, so the switch has to sit
 * above all of that — and it has to be applied BEFORE the first paint, or a
 * desktop user watches the app render as a phone column and then jump. That
 * is why index.html reads this key inline, the same way it reads the text
 * scale, and why the value is stored as the attribute's own text rather than
 * as something needing interpretation.
 *
 * NOT a media query. "I am on a wide screen" and "I want the wide layout" are
 * different claims: a tablet on a pub wall is 1024px and wants the phone
 * column, and somebody with a browser window open beside a spreadsheet may
 * not want it either. Ben asked for an option, and an option is what this is.
 */
const KEY = 'cueforge.width';

export const WIDTHS = [
  { id: 'phone', label: 'Phone', hint: 'One column, as it has always been.' },
  { id: 'desktop', label: 'Desktop',
    hint: 'Uses the width of the window. Better on a laptop or a monitor.' },
];

export function widthMode() {
  try {
    return localStorage.getItem(KEY) === 'desktop' ? 'desktop' : 'phone';
  } catch (e) { return 'phone'; }
}

export function setWidthMode(mode) {
  const wanted = mode === 'desktop' ? 'desktop' : 'phone';
  // Set rather than toggled off: `[data-width=phone]` is a real state the CSS
  // can key on, and an absent attribute would make "never chose" and "chose
  // phone" indistinguishable to anything that later wants to tell them apart.
  document.documentElement.setAttribute('data-width', wanted);
  try { localStorage.setItem(KEY, wanted); } catch (e) { /* fine */ }
}

/** The picker, in Settings beside the text size — the other thing here that
 * changes how the app looks rather than what it does. */
export function WidthMode({ onChanged }) {
  const now = widthMode();
  return html`
    <div class="stack" data-testid="width-mode">
      <label>Layout</label>
      <div class="seg">
        ${WIDTHS.map((w) => html`
          <button key=${w.id} aria-pressed=${now === w.id}
                  data-testid=${`width-${w.id}`}
                  onClick=${() => { setWidthMode(w.id); onChanged(); }}>
            ${w.label}
          </button>`)}
      </div>
      <p class="sub">
        ${WIDTHS.find((w) => w.id === now).hint}
      </p>
    </div>`;
}
